import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import snackNotifications from "../snackbar";

import { store } from "../../store";
import { SettingGroupType } from "../../models/SettingGroupType";
import { defaultLocale } from "../../constants/languages";
import i18n from "../../i18n";

interface IBackendError {
    displayMsgKey: string;
    correlationId: string;
    errorMsg: string;
}

function showServerError(error: IBackendError) {
    const systemConfigs =
        store.getState().systemConfigs[
        SettingGroupType.frontOfficeSystemNotifications
        ];
    const errorNotification = systemConfigs?.find(
        (x) => x.key === error.displayMsgKey
    );

    snackNotifications.serverError(
        errorNotification?.value || error.displayMsgKey,
        {
            autoHideDuration: null,
            errorDescription: error.errorMsg,
            errorId: error.correlationId,
        }
    );
}

axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<IBackendError>) => {
        if (error.response !== undefined) {
            if (error.response.data instanceof Blob) {
                const reponseData = await new Response(
                    error.response.data
                ).text();
                const parsed = JSON.parse(reponseData) as IBackendError;

                if (!parsed) return;
                showServerError(parsed);
                return;
            }

            if (error.response.data.displayMsgKey) {
                showServerError(error.response.data);
            }
        }

        return Promise.reject(error);
    }
);

const klasisAxios = axios.create();

klasisAxios.interceptors.request.use((config) => {
    const url = new URL(config.url ?? '');
    const localeCode = url.searchParams.get('localeCode');
    const currentLocale = localeCode ?? i18n.language ?? defaultLocale;

    if (!config.headers) {
        config.headers = {};
    }

    config.headers["Accept-Language"] = currentLocale;

    return config;
});

klasisAxios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<IBackendError>) => {
        if (error.response !== undefined) {
            if (error.response.data instanceof Blob) {
                const reponseData = await new Response(
                    error.response.data
                ).text();
                const parsed = JSON.parse(reponseData) as IBackendError;

                if (!parsed) return;
                showServerError(parsed);
                return;
            }

            if (error.response.data.displayMsgKey) {
                showServerError(error.response.data);
            }
        }

        return Promise.reject(error);
    }
);

export function handleAxiosErrorDefault(error: AxiosError) {
    // If developer provided error handler use it.
    const response = error.response;

    if (!response) return;

    const data = response.data as IKlasisRestError;

    let title: string = data.title;
    if (!title || title === "")
        //title = Localize(Translations.common.headers.error);
        title = "Kļūda";

    let details: string = data.error;
    if (!details || details === "" || response.status === 500)
        //details = Localize(Translations.common.errors.genericServerError);
        details = "Notikusi neparedzēta kļūda";

    showServerError({
        correlationId: data.traceId,
        displayMsgKey: data.title,
        errorMsg: data.error
    });
}

// @deprecated DON'T USE RestService, USE AXIOS INSTEAD
export class RestService {
    public static Post = <T>(
        route: string,
        query: unknown,
        callBack: (arg0: T) => void,
        errorCallBack?: (response: AxiosError) => void,
        finallyFn?: () => void
    ): void => {
        klasisAxios
            .post(route, query)
            // If error occurs on server side, handle it using global method.
            .catch((error: AxiosError) => {
                if (errorCallBack) {
                    // Intercept errors to transform to array for UI rendering
                    //if (error.response?.data && error.response.status === 400) {
                    //    error.response.data.errors = errorArrayHelper(
                    //        error.response.data.errors
                    //    );
                    //}
                    errorCallBack(error);
                } else {
                    handleAxiosErrorDefault(error);
                }
            })
            .then((response: AxiosResponse | void) => {
                if (response != null) callBack(response.data);
            })
            .finally(() => {
                if (finallyFn) finallyFn();
            });
    };

    public static Put = <T>(
        route: string,
        query: unknown,
        callBack: (arg0: T) => void,
        errorCallBack?: (response: AxiosError) => void,
        config?: AxiosRequestConfig
    ): void => {
        klasisAxios
            .put(route, query, config)
            // If error occurs on server side, handle it using global method.
            .catch((error: AxiosError) => {
                if (errorCallBack) {
                    // Intercept errors to transform to array for UI rendering
                    //if (error.response?.data && error.response.status === 400) {
                    //    error.response.data.errors = errorArrayHelper(
                    //        error.response.data.errors
                    //    );
                    //}
                    errorCallBack(error);
                } else {
                    handleAxiosErrorDefault(error);
                }
            })
            .then((response: AxiosResponse | void) => {
                if (response != null) callBack(response.data);
            });
    };

    public static Patch = <T>(
        route: string,
        query: string,
        callBack: (arg0: T) => void,
        errorCallBack?: (response: AxiosError) => void
    ): void => {
        klasisAxios
            .patch(route, query)
            // If error occurs on server side, handle it using global method.
            .catch((error: AxiosError) => {
                if (errorCallBack) errorCallBack(error);
                else handleAxiosErrorDefault(error);
            })
            .then((response: AxiosResponse | void) => {
                if (response != null) callBack(response.data);
            });
    };

    public static Get = <T>(
        route: string,
        callBack: (arg0: T) => void,
        errorCallBack?: (response: AxiosError) => void,
        finallyFn?: () => void
    ): void => {
        klasisAxios
            .get(route)
            // If error occurs on server side, handle it using global method.
            .catch((error: AxiosError) => {
                if (errorCallBack) {
                    errorCallBack(error);
                } else {
                    handleAxiosErrorDefault(error);
                }
            })
            .then((response: AxiosResponse | void) => {
                if (response != null) {
                    callBack(response.data);
                }
            })
            .finally(() => {
                if (finallyFn) finallyFn();
            });
    };

    public static Delete = <T>(
        route: string,
        callBack: (arg0: T) => void,
        errorCallBack?: (response: AxiosError) => void,
        finallyFn?: () => void
    ): void => {
        klasisAxios
            .delete(route)
            // If error occurs on server side, handle it using global method.
            .catch((error: AxiosError) => {
                if (errorCallBack) errorCallBack(error);
                else handleAxiosErrorDefault(error);
            })
            .then((response: AxiosResponse | void) => {
                if (response != null) {
                    callBack(response.data);
                }
            })
            .finally(() => {
                if (finallyFn) finallyFn();
            });
    };
}

export interface IFileDownloadOptionsLoose {
    route: string;
    fileName: string;
}

export interface IKlasisRestError {
    error: string;
    status: number;
    title: string;
    /**Correlation ID in the GUID format. */
    traceId: string;
    /** Status code description like https://tools.ietf.org/html/rfc7231#section-6.5.7 */
    type: string;
}

type MimeType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";